/*
  NEXORA — Wishlist / Favoris

  Fonctionne SANS application tierce et SANS compte client :
  les favoris sont enregistrés dans le navigateur du visiteur (localStorage).

  Ce que ça fait vraiment :
  - bouton coeur sur chaque carte produit et sur la fiche produit
  - compteur dans le header
  - page "Mes favoris" qui affiche les pièces suivies
  - synchronisation entre les onglets ouverts

  Limite assumée (à dire au marchand, pas à cacher) :
  les favoris sont liés au navigateur, pas au compte client. Changer d'appareil
  = repartir de zéro. Pour une synchronisation multi-appareils, il faut une app
  ou un développement serveur.
*/
(() => {
  const KEY = 'nx:wishlist:v1';
  const EVT = 'nx:wishlist:change';


  /* Echappement HTML : les donnees du localStorage sont injectees dans le DOM.
     Sans echappement, un titre produit contenant du HTML deviendrait executable (XSS). */
  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

  /* Seules des URLs internes relatives sont acceptees au rendu. */
  const safePath = (value) => {
    const v = String(value ?? '');
    return v.startsWith('/') && !v.startsWith('//') ? v : '#';
  };

  const read = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* quota plein ou mode privé : on n'empêche pas la navigation */
    }
    document.dispatchEvent(new CustomEvent(EVT, { detail: { list } }));
  };

  const has = (handle) => read().some((i) => i.handle === handle);

  const toggle = (item) => {
    const list = read();
    const index = list.findIndex((i) => i.handle === item.handle);
    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.unshift({ ...item, added: Date.now() });
    }
    write(list);
    return index === -1;
  };

  const paintButton = (button) => {
    const active = has(button.dataset.nxWishlist);
    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    const label = active ? button.dataset.nxLabelOn : button.dataset.nxLabelOff;
    if (label) button.setAttribute('aria-label', label);
  };

  const paintCount = () => {
    const count = read().length;
    document.querySelectorAll('[data-nx-wishlist-count]').forEach((node) => {
      node.textContent = count > 0 ? String(count) : '';
      node.hidden = count === 0;
    });
  };

  const paintAll = (scope = document) => {
    scope.querySelectorAll('[data-nx-wishlist]').forEach(paintButton);
    paintCount();
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-nx-wishlist]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    toggle({
      handle: button.dataset.nxWishlist,
      title: button.dataset.nxTitle || '',
      url: button.dataset.nxUrl || '',
      image: button.dataset.nxImage || '',
      price: button.dataset.nxPrice || '',
      vendor: button.dataset.nxVendor || '',
    });

    paintAll();
    renderPage();
  });

  /* Synchronisation entre onglets ouverts */
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) {
      paintAll();
      renderPage();
    }
  });


  /* Vérité produit (audit 4.10) : le localStorage peut être périmé.
     On confronte chaque favori à la boutique : produit supprimé -> "Annonce
     indisponible" ; plus en stock -> "Vendu" ; prix changé -> prix rafraîchi.
     En cas d'échec réseau, on n'affiche rien de faux : la carte reste telle quelle. */
  function verify(root, grid, list) {
    const fmt = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
          style: 'currency',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
        }).format(cents / 100);
      } catch (e) { return null; }
    };
    list.forEach((item) => {
      if (!item.handle) return;
      fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}products/${encodeURIComponent(item.handle)}.js`)
        .then((r) => {
          const card = grid.querySelector(`[data-nx-wishlist-card="${CSS.escape(item.handle)}"]`);
          if (!card) return null;
          const status = card.querySelector('[data-nx-wishlist-status]');
          if (r.status === 404) {
            if (status) { status.textContent = root.dataset.nxUnavailableLabel || 'Annonce indisponible'; status.hidden = false; }
            card.classList.add('is-unavailable');
            return null;
          }
          return r.ok ? r.json().then((prod) => {
            if (!prod.available) {
              if (status) { status.textContent = root.dataset.nxSoldLabel || 'Vendu'; status.hidden = false; }
              card.classList.add('is-sold');
            }
            const priceNode = card.querySelector('[data-nx-wishlist-price]');
            const live = fmt(prod.price);
            if (priceNode && live) priceNode.textContent = live;
          }) : null;
        })
        .catch(() => { /* hors-ligne : ne rien affirmer */ });
    });
  }

  /* Échappement : les données du localStorage sont réinjectées en HTML.
     Même si elles proviennent de la boutique, on les traite comme non sûres. */
  const esc = (v) => String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const escUrl = (v) => {
    const s = String(v == null ? '' : v);
    return /^(https?:)?\//.test(s) ? esc(s) : '';
  };


  /* Revalidation (audit 4.10) : au chargement de la page favoris, chaque piece
     est verifiee aupres de Shopify (/products/handle.js) :
     - 404            -> marquee « Retiree de la vente » (lien desactive)
     - available=false-> badge « Vendue »
     - prix change    -> mis a jour silencieusement
     Reseau indisponible -> on garde les donnees locales, sans bloquer. */
  let revalidated = false;
  async function revalidatePage(root) {
    if (revalidated) return;
    revalidated = true;
    const list = read();
    if (!list.length) return;
    root.setAttribute('data-nx-loading', 'true');
    const money = (cents) => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
          style: 'currency',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR',
        }).format(cents / 100);
      } catch (e) {
        return `${(cents / 100).toFixed(2)}`;
      }
    };
    await Promise.all(
      list.map(async (item) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(item.handle)}.js`);
          if (res.status === 404) {
            item.gone = true;
            return;
          }
          if (!res.ok) return;
          const data = await res.json();
          item.gone = false;
          item.available = data.available;
          if (typeof data.price === 'number') item.price = money(data.price);
          if (data.title) item.title = data.title;
        } catch (e) {
          /* hors-ligne : on n'invente rien, on garde l'etat connu */
        }
      })
    );
    write(list);
    root.removeAttribute('data-nx-loading');
  }

  /* Page "Mes favoris" */
  function renderPage() {
    const root = document.querySelector('[data-nx-wishlist-page]');
    if (!root) return;
    revalidatePage(root);

    const list = read();
    const grid = root.querySelector('[data-nx-wishlist-grid]');
    const empty = root.querySelector('[data-nx-wishlist-empty]');
    if (!grid || !empty) return;

    if (!list.length) {
      grid.innerHTML = '';
      grid.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    grid.hidden = false;
    grid.innerHTML = list
      .map((item) => {
        const handle = escapeHtml(item.handle);
        const title = escapeHtml(item.title);
        const url = safePath(item.url);
        const image = safePath(item.image);
        const price = escapeHtml(item.price);
        const vendorName = escapeHtml(item.vendor);
        const gone = item.gone === true;
        const sold = item.available === false;
        const img = item.image
          ? `<img src="${image}" alt="" loading="lazy" width="400" height="400">`
          : '<span class="nx-wishlist-card__placeholder" aria-hidden="true"></span>';
        const vendor = vendorName ? `<p class="nx-wishlist-card__vendor">${vendorName}</p>` : '';
        const badge = gone
          ? `<p class="nx-wishlist-card__state">${escapeHtml(root.dataset.nxGoneLabel || 'Retirée de la vente')}</p>`
          : sold
            ? `<p class="nx-wishlist-card__state">${escapeHtml(root.dataset.nxSoldLabel || 'Vendue')}</p>`
            : '';
        const media = gone
          ? `<span class="nx-wishlist-card__media nx-wishlist-card__media--gone">${img}</span>`
          : `<a class="nx-wishlist-card__media" href="${url}">${img}</a>`;
        const heading = gone ? `<span>${title}</span>` : `<a href="${url}">${title}</a>`;
        return `
          <article class="nx-wishlist-card${gone || sold ? ' nx-wishlist-card--muted' : ''}">
            ${media}
            <div class="nx-wishlist-card__body">
              ${vendor}
              <h3 class="nx-wishlist-card__title">${heading}</h3>
              <p class="nx-wishlist-card__price">${price}</p>
              ${badge}
            </div>
            <button
              type="button"
              class="nx-wishlist-card__remove"
              data-nx-wishlist="${handle}"
            >${escapeHtml(root.dataset.nxRemoveLabel || 'Retirer')}</button>
          </article>`;
      })
      .join('');

    verify(root, grid, list);
  }

  const boot = () => {
    paintAll();
    renderPage();
  };

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', (event) => paintAll(event.target));

  window.NexoraWishlist = { read, has, toggle, count: () => read().length };
})();
